import { lumi } from '@/lib/lumi'

// ── Credenciais permanentes do Admin Master ───────────────────────────────
export const ADMIN_USERNAME = 'Admin'
export const ADMIN_PASSWORD = 'Admin200%'
export const ADMIN_EMAIL    = 'admin.sigestao@prefeitura.local'

// Chave de sessão no localStorage (autenticação própria, sem Lumi Auth)
export const SESSION_KEY = 'geoa_session'

// Duração máxima da sessão: 8 horas em milissegundos
const SESSION_TTL_MS = 8 * 60 * 60 * 1000

interface SessionEnvelope {
  user: SessionUser
  expiresAt: number
}

export interface SessionUser {
  userId: string
  userName: string
  email?: string
  cargo?: string
  userRole: 'ADMIN' | 'USER'
}

// ── Garante que o Admin Master existe SEMPRE no banco ─────────────────────
// Recria automaticamente se for apagado manualmente. Chamado no arranque.
let _done = false
export async function bootstrapAdminMaster(): Promise<void> {
  if (_done) return
  _done = true
  try {
    const { list } = await lumi.entities.usuarios_sistema.list({
      filter: { userName: ADMIN_USERNAME },
      limit : 1,
    })

    const existing = list?.[0] as { _id?: string; senha?: string } | undefined

    if (existing && existing._id) {
      // Garante que a senha e o papel estão sempre corretos
      if (existing.senha !== ADMIN_PASSWORD) {
        await lumi.entities.usuarios_sistema.update(existing._id, {
          senha    : ADMIN_PASSWORD,
          userRole : 'ADMIN',
          ativo    : true,
          updatedAt: new Date().toISOString(),
        })
        console.info('[GEOA] bootstrapAdminMaster → senha do Admin restaurada.')
      } else {
        console.info('[GEOA] bootstrapAdminMaster → Admin já existe e está correto.')
      }
      return
    }

    // Não existe → recria o Admin Master
    await lumi.entities.usuarios_sistema.create({
      userId   : 'admin-master',
      userName : ADMIN_USERNAME,
      senha    : ADMIN_PASSWORD,
      email    : ADMIN_EMAIL,
      cargo    : 'Administrador do Sistema',
      userRole : 'ADMIN',
      ativo    : true,
      creator  : ADMIN_USERNAME,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
    console.info('[GEOA] bootstrapAdminMaster → Admin Master recriado.')
  } catch (e) {
    console.error('[GEOA] bootstrapAdminMaster → erro:', e)
  }
}

// ── Login próprio: valida usuário/senha direto no banco ───────────────────
// Retorna o utilizador da sessão em caso de sucesso, ou null em falha.
export async function loginComBanco(
  userName: string,
  senha: string,
): Promise<SessionUser | null> {
  try {
    const { list } = await lumi.entities.usuarios_sistema.list({
      filter: { userName },
      limit : 50,
    })

    // Procura correspondência exata (case-insensitive no nome, senha exata)
    const nomeNorm = userName.trim().toLowerCase()
    const match = (list ?? []).find((u) => {
      const un = String((u as { userName?: string }).userName ?? '').trim().toLowerCase()
      const pw = String((u as { senha?: string }).senha ?? '')
      const ativo = (u as { ativo?: boolean }).ativo !== false
      return un === nomeNorm && pw === senha && ativo
    }) as
      | { _id: string; userId?: string; userName: string; email?: string; cargo?: string; userRole?: string }
      | undefined

    if (!match) return null

    return {
      userId  : match.userId ?? match._id,
      userName : match.userName,
      email    : match.email,
      cargo    : match.cargo,
      userRole : (match.userRole === 'ADMIN' ? 'ADMIN' : 'USER'),
    }
  } catch (e) {
    console.error('[GEOA] loginComBanco → erro:', e)
    return null
  }
}

// ── Persiste / lê / limpa a sessão no localStorage ────────────────────────
export function salvarSessao(user: SessionUser): void {
  const envelope: SessionEnvelope = {
    user,
    expiresAt: Date.now() + SESSION_TTL_MS,
  }
  localStorage.setItem(SESSION_KEY, JSON.stringify(envelope))
}

export function lerSessao(): SessionUser | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Suporte ao formato legado (sem expiresAt) — invalida imediatamente
    if (!parsed.expiresAt) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    const envelope = parsed as SessionEnvelope
    if (Date.now() > envelope.expiresAt) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return envelope.user
  } catch {
    localStorage.removeItem(SESSION_KEY)
    return null
  }
}

export function limparSessao(): void {
  localStorage.removeItem(SESSION_KEY)
}
